import {
  useLocation,
  useMatch,
  useNavigate,
  useRoutes,
} from 'react-router-dom';
import WorkspaceShell from '@/components/layout/WorkspaceShell';

import ComponentListView from './ComponentsList/ComponentsListView';
import InventorySidebar from './ComponentsList/InventorySidebar';
import AssistantChatView from './Assistant/AssistantChatView';
import CoverageKitView from './CoverageKit/CoverageKitView';
import ComponentInventoryView from './ComponentInventory/ComponentInventoryView';
import SidebarNavigation from '../common/Sidebar';
import { useSelector } from 'react-redux';
import { RootState } from '@/state/store';
import ComponentBuildDiff from './ComponentBuildDiff/ComponentBuildDiffView';
import ComponentCoverageView from './ComponentCoverage/ComponentCoverageView';
import ComponentTestIntelligenceView from './ComponentTestIntelligence/ComponentTestIntelligenceView';
import ComponentOnboardingView from './ComponentOnboarding/ComponentOnboardingView';
import TestOptimizationPage from './TestOptimization/TestOptimizationPage';
import TestCasesPage from './TestOptimization/TestCasesPage';
import CallGraphPage from './CallGraph/CallGraphPage';
import ManualTestingView from './ManualTesting/ManualTestingView';
// import ReleasesView from './ManualTesting/ReleasesView';
// import ReleaseExecutionPage from './ManualTesting/ReleaseExecutionPage';
import ComponentCoverageViewAutomation from './ComponentCoverage/ComponentCoverageViewAutomation';
import ComponentCoverageViewManual from './ComponentCoverage/ComponentCoverageViewManual';
import ComponentCoverageHistoryView from './ComponentCoverage/ComponentCoverageHistoryView';
import ComponentCoverageBuildRunsView from './ComponentCoverage/ComponentCoverageBuildRunsView';

const SIDEBAR_ROUTES: Record<string, string> = {
  '2-0': '/sia/components/code_analysis/inventory',
  '2-1': '/sia/components/code_analysis/build_analysis',
  '2-2': '/sia/components/code_analysis/test-cases',
  '2-3': '/sia/components/code_analysis/call-graph',
  '3-0-0': '/sia/components/coverage_analysis/dashboard',
  '3-0-1': '/sia/components/coverage_analysis/manual',
  '3-0-2': '/sia/components/coverage_analysis/automation',
  '3-0-3': '/sia/components/coverage_analysis/trend',
  '3-1': '/sia/components/coverage_analysis/test-gap-analysis',
  '4': '/sia/components/manual-testing',
  // '5': '/sia/components/releases',
};

const ROUTE_TO_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(SIDEBAR_ROUTES).map(([key, route]) => [route, key]),
);

const getActiveKey = (pathname: string): string => {
  const sorted = Object.entries(ROUTE_TO_KEY).sort(
    ([a], [b]) => b.length - a.length,
  );
  const match = sorted.find(([route]) => pathname.startsWith(route));
  return match ? match[1] : '2-0';
};

const RELEASES_ROUTE = '/sia/components/releases';

const getCurrentDbRepoId = (pathname: string): string | undefined => {
  const releasesChild = pathname.startsWith(`${RELEASES_ROUTE}/`)
    ? pathname.slice(RELEASES_ROUTE.length + 1).split('/')[0]
    : '';
  if (releasesChild && releasesChild !== 'execution') {
    return releasesChild;
  }

  const base = Object.values(SIDEBAR_ROUTES).find(route =>
    pathname.startsWith(route),
  );
  if (!base) return undefined;
  const rest = pathname.slice(base.length).replace(/^\//, '');
  const segment = rest.split('/')[0] || undefined;
  if (base === RELEASES_ROUTE && segment === 'execution') {
    return undefined;
  }
  return segment;
};

const SmartAI = () => {
  const isListView = Boolean(
    useMatch({ path: '/sia/components', end: true }),
  );
  const isAssistant = Boolean(
    useMatch({ path: '/sia/assistant', end: true }),
  );
  const isCoverageKit = Boolean(
    useMatch({ path: '/sia/coveragekit', end: true }),
  );
  const isUtilityView = isAssistant || isCoverageKit;

  const location = useLocation();
  const navi = useNavigate();

  const selectedComponent = useSelector(
    (state: RootState) => state.userSelectedComponentSlice,
  );
  const activeKey = getActiveKey(location.pathname);
  const currentDbRepoId =
    getCurrentDbRepoId(location.pathname) ??
    (selectedComponent?.dbRepoId?.trim() || undefined);
  const routes = useRoutes([
    {
      path: 'onboarding',
      element: <ComponentOnboardingView />,
    },
    {
      path: 'assistant',
      element: <AssistantChatView />,
    },
    {
      path: 'coveragekit',
      element: <CoverageKitView />,
    },
    {
      path: 'components',
      element: <ComponentListView />,
    },
    {
      path: 'components/code_analysis/inventory/:dbRepoId',
      element: <ComponentInventoryView />,
    },
    {
      path: 'components/code_analysis/build_analysis/:dbRepoId',
      element: <ComponentBuildDiff />,
    },
    {
      path: 'components/code_analysis/test-optimization/:dbRepoId',
      element: <TestOptimizationPage />,
    },
    {
      path: 'components/code_analysis/test-cases/:dbRepoId',
      element: <TestCasesPage />,
    },
    {
      path: 'components/code_analysis/call-graph/:dbRepoId',
      element: <CallGraphPage />,
    },
    {
      path: 'components/coverage_analysis/dashboard/:dbRepoId',
      element: <ComponentCoverageView />,
    },
    {
      path: 'components/coverage_analysis/manual/:dbRepoId',
      element: <ComponentCoverageViewManual />,
    },
    {
      path: 'components/coverage_analysis/automation/:dbRepoId',
      element: <ComponentCoverageViewAutomation />,
    },
    {
      path: 'components/coverage_analysis/trend/:dbRepoId/build/:scanId',
      element: <ComponentCoverageBuildRunsView />,
    },
    {
      path: 'components/coverage_analysis/trend/:dbRepoId',
      element: <ComponentCoverageHistoryView />,
    },
    {
      path: 'components/coverage_analysis/test-gap-analysis/:dbRepoId',
      element: <ComponentTestIntelligenceView />,
    },
    {
      path: 'components/manual-testing/:dbRepoId',
      element: <ManualTestingView />,
    },
    // {
    //   path: 'components/releases/:dbRepoId/execution',
    //   element: <ReleaseExecutionPage />,
    // },
    // {
    //   path: 'components/releases/:dbRepoId',
    //   element: <ReleasesView />,
    // },
    // {
    //   path: 'components/releases',
    //   element: <ReleasesView />,
    // },
  ]);

  const handleSideBarCallback = (key: string) => {
    const route = SIDEBAR_ROUTES[key];
    if (!route) return;
    navi(currentDbRepoId ? `${route}/${currentDbRepoId}` : route);
  };

  return (
    <WorkspaceShell
      expandSidebarOnHover={!(isListView || isUtilityView)}
      sidebar={
        isListView || isUtilityView ? (
          <InventorySidebar />
        ) : (
          <SidebarNavigation
            id={activeKey}
            handleSideBarCallback={handleSideBarCallback}
          />
        )
      }
      mainClassName={
        isUtilityView ? 'flex w-full flex-col overflow-hidden' : 'w-full'
      }>
      {routes}
    </WorkspaceShell>
  );
};

export default SmartAI;
